import {BaseComponent} from '../base/component.base.js';
import template from './cold-start-splash.template.html';
import styles from './cold-start-splash.styles.css';

/**
 * Fullscreen "warming up" splash shown between successful login and the first
 * dashboard render. Exists because the backend runs as a GraalVM native-image
 * scale-to-zero container — the first request after a sleep takes ~1.2s for
 * cold start, which would otherwise look like the app is hanging.
 *
 * The splash advances through three stages (Waking up → Fetching templates →
 * Ready) on a fixed cadence; it hides as soon as the wrapped promise resolves
 * AND a minimum duration has elapsed (so it never flickers on warm starts).
 * A max-duration safety net prevents the splash from sticking forever if the
 * promise hangs.
 */
export class ColdStartSplashComponent extends BaseComponent {
    static #STAGE_DURATION_MS = 1000;
    static #DEFAULT_MIN_DURATION_MS = 700;
    // A scale-to-zero backend with a waking database can take the full
    // advertised "up to 30 seconds" cold start (plus the follow-up template
    // load) to answer. The safety net must outlast that window, otherwise the
    // splash dismisses while auth is still pending and exposes the login form's
    // slow-start hint underneath. It only bails earlier when the wrapped
    // promise genuinely hangs past this ceiling.
    static #DEFAULT_MAX_DURATION_MS = 40000;
    static #READY_HOLD_MS = 420;
    static #FADE_OUT_MS = 260;

    #stageTimers = [];
    #currentStage = 0;
    #runId = 0;

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.hidden = true;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#clearStageTimers();
        this.#runId++;
    }

    /**
     * Show the splash while the given promise resolves. Honors a minimum
     * duration (prevents flicker on warm cache hits) and a maximum duration
     * (safety net if the promise hangs). The promise is awaited even if it
     * rejects. A concurrent `runWhile` call cancels the previous run.
     *
     * @param {Promise<unknown>} promise
     * @param {{minDurationMs?: number, maxDurationMs?: number}} [opts]
     * @returns {Promise<void>}
     */
    async runWhile(promise, opts = {}) {
        const minDuration = opts.minDurationMs ?? ColdStartSplashComponent.#DEFAULT_MIN_DURATION_MS;
        const maxDuration = opts.maxDurationMs ?? ColdStartSplashComponent.#DEFAULT_MAX_DURATION_MS;
        const stageDuration = ColdStartSplashComponent.#STAGE_DURATION_MS;

        const runId = ++this.#runId;
        const isCurrent = () => this.#runId === runId;

        this.#clearStageTimers();

        this.hidden = false;
        // Force layout flush so the opacity transition runs from 0 — browsers
        // may otherwise coalesce the hidden→visible swap into a single paint.
        void this.offsetWidth;
        this.classList.add('is-visible');
        this.#setStage(0);

        // Rotator advances through every stage except the final one — that one
        // is reserved as the "ready" finale and only set once the wrapped
        // promise resolves. Holding the second-to-last stage while waiting
        // avoids declaring "Ready" before anything is actually ready.
        const stageCount = this.$$('.splash-stage').length;
        this.#stageTimers = [];
        for (let i = 1; i < stageCount - 1; i++) {
            const index = i;
            this.#stageTimers.push(setTimeout(() => {
                if (isCurrent()) this.#setStage(index);
            }, stageDuration * i));
        }

        const start = Date.now();

        await Promise.race([
            Promise.resolve(promise).then(() => undefined, () => undefined),
            new Promise((resolve) => setTimeout(resolve, maxDuration)),
        ]);
        if (!isCurrent()) return;

        this.#clearStageTimers();

        const elapsed = Date.now() - start;
        if (elapsed < minDuration) {
            await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
            if (!isCurrent()) return;
        }

        // Brief victory hold on the final stage so a fast resolve doesn't jump
        // straight from an early stage to fade-out.
        const lastStage = Math.max(0, this.$$('.splash-stage').length - 1);
        this.#setStage(lastStage);
        this.classList.add('is-ready');
        await new Promise((resolve) => setTimeout(resolve, ColdStartSplashComponent.#READY_HOLD_MS));
        if (!isCurrent()) return;

        this.classList.remove('is-visible');
        await new Promise((resolve) => setTimeout(resolve, ColdStartSplashComponent.#FADE_OUT_MS));
        if (!isCurrent()) return;

        this.hidden = true;
        this.classList.remove('is-ready');
        this.#setStage(0);
    }

    #setStage(index) {
        this.#currentStage = index;
        const stages = this.$$('.splash-stage');
        stages.forEach((stage, i) => {
            stage.classList.toggle('is-current', i === index);
            stage.classList.toggle('is-done', i < index);
        });
    }

    #clearStageTimers() {
        for (const timer of this.#stageTimers) {
            clearTimeout(timer);
        }
        this.#stageTimers = [];
    }
}

customElements.define('cold-start-splash', ColdStartSplashComponent);
